import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useDock } from './ParamDock';
import { BottomSheet } from './BottomSheet';
import { formatLength, degreesToPitch, stepControl, } from '../../util/units';
import { DockButton } from "./DockButton";
import { t } from "../locales";
const css = `
.params-control-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 14px;
    border-radius: 10px;
    border: 0.5px solid #ccc;
    background: #fff;
    color: #333;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.1s;
    white-space: nowrap;
}

.params-control-trigger:hover {
    border-color: #1a1a1a;
}

.params-control-trigger:active {
    transform: scale(0.97);
}

.params-control-trigger--active {
    background: #1a1a1a;
    color: #fff;
    border-color: #1a1a1a;
}

.params-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px 0;
}

.params-unit-toggle {
    display: flex;
    background: #f1f5f9;
    border-radius: 6px;
    padding: 2px;
    gap: 2px;
    margin: 0 0 12px;
    width: fit-content;
    align-self: flex-end;
}

.params-unit-btn {
    height: 22px;
    padding: 0 10px;
    border: none;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    background: transparent;
    color: #94a3b8;
    line-height: 1;
}

.params-unit-btn--active {
    background: #fff;
    color: #1a1a1a;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}

.params-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 4px;
    gap: 12px;
    border-bottom: 0.5px solid #f1f5f9;
}

.params-row:last-of-type {
    border-bottom: none;
}

.params-label {
    font-size: 13px;
    color: #1a202c;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.params-control {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.params-btn {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #f1f5f9;
    border: 0.5px solid #e2e8f0;
    color: #1a202c;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.12s, transform 0.1s;
    padding: 0;
    line-height: 1;
    flex-shrink: 0;
}

.params-btn:hover {
    background: #e2e8f0;
}

.params-btn:active {
    transform: scale(0.9);
    background: #cbd5e0;
}

.params-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    transform: none;
}

.params-value {
    min-width: 70px;
    height: 28px;
    border-radius: 6px;
    background: #f8fafc;
    border: 0.5px solid #e2e8f0;
    color: #1a202c;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    padding: 0 6px;
    font-weight: 500;
}

.params-divider {
    height: 1px;
    background: #f1f5f9;
    margin: 10px 0 6px;
}

.params-group-label {
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #a0aec0;
    padding: 4px 4px 6px;
    font-weight: 700;
}

.params-calculate {
    margin-top: 16px;
    width: 100%;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #2563eb, #1e40af);
    border: none;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
    box-shadow: 0 4px 16px rgba(37, 99, 235, 0.3);
}

.params-calculate:hover {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.45);
}

.params-calculate:active {
    transform: scale(0.98);
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
}
`;
const DOCK_ID = 'params-control';
function groupControls(controls) {
    const main = [];
    const overhang = [];
    for (const c of controls) {
        if (c.isOverhang)
            overhang.push(c);
        else
            main.push(c);
    }
    return { main, overhang };
}
function ParamRow({ control, units, lang }) {
    const displayValue = control.isAngle
        ? (units === 'ft' ? degreesToPitch(control.value) : `${control.value.toFixed(1)}°`)
        : formatLength(control.value, units, lang);
    const atMin = control.min !== undefined && control.value <= control.min;
    const atMax = control.max !== undefined && control.value >= control.max;
    return (_jsxs("div", { className: "params-row", children: [_jsx("span", { className: "params-label", children: control.label }), _jsxs("div", { className: "params-control", children: [_jsx("button", { className: "params-btn", onClick: () => control.onChange(stepControl(control, -1)), disabled: atMin, children: "\u2212" }), _jsx("div", { className: "params-value", children: displayValue }), _jsx("button", { className: "params-btn", onClick: () => control.onChange(stepControl(control, 1)), disabled: atMax, children: "+" })] })] }));
}
export function ParamsControl({ controls, onCalculate, onSave, title, btnText, btnTextSave, units = 'm', lang = 'en', onUnitsChange, label, }) {
    const { isOpen, toggle, close } = useDock(DOCK_ID);
    const { main, overhang } = groupControls(controls);
    const triggerLabel = label ?? title;
    const handleCalculate = () => {
        onCalculate?.();
        close();
    };
    const handleSave = () => {
        onSave?.();
        close();
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: css }), _jsx(DockButton, { icon: _jsx(ParamsIcon, {}), label: triggerLabel, title: triggerLabel, active: isOpen, onClick: toggle }), _jsxs(BottomSheet, { open: isOpen, onClose: close, title: title ?? t(lang, 'params.title'), children: [onUnitsChange && (_jsxs("div", { className: "params-unit-toggle", children: [_jsx("button", { className: `params-unit-btn ${units === 'm' ? 'params-unit-btn--active' : ''}`, onClick: () => onUnitsChange('m'), children: "M" }), _jsx("button", { className: `params-unit-btn ${units === 'ft' ? 'params-unit-btn--active' : ''}`, onClick: () => onUnitsChange('ft'), children: "FT" })] })), _jsxs("div", { className: "params-list", children: [main.map((c, i) => _jsx(ParamRow, { control: c, units: units, lang: lang }, i)), overhang.length > 0 && (_jsxs(_Fragment, { children: [_jsx("div", { className: "params-divider" }), _jsx("div", { className: "params-group-label", children: t(lang, 'params.overhangs') }), overhang.map((c, i) => _jsx(ParamRow, { control: c, units: units, lang: lang }, i))] }))] }), onSave && btnTextSave && (_jsx("button", { className: "params-calculate", onClick: handleSave, children: btnTextSave ?? t(lang, 'params.calculate') })), onCalculate && (_jsx("button", { className: "params-calculate", onClick: handleCalculate, children: btnText }))] })] }));
}
const ParamsIcon = () => (_jsxs("svg", { width: "36", height: "36", viewBox: "0 0 100 100", fill: "none", stroke: "currentColor", strokeWidth: "5", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("line", { x1: "74.07", y1: "30", x2: "84.07", y2: "30" }), _jsx("line", { x1: "15.93", y1: "30", x2: "44.89", y2: "30" }), _jsx("line", { x1: "52.41", y1: "50", x2: "84.07", y2: "50" }), _jsx("line", { x1: "15.93", y1: "50", x2: "22.52", y2: "50" }), _jsx("line", { x1: "82.63", y1: "70", x2: "84.07", y2: "70" }), _jsx("line", { x1: "15.93", y1: "70", x2: "53.3", y2: "70" }), _jsx("circle", { cx: "68.06", cy: "70", r: "8.59" }), _jsx("circle", { cx: "37.13", cy: "50", r: "8.59" }), _jsx("circle", { cx: "59.47", cy: "30", r: "8.59" })] }));
